import { useQueryClient } from "@tanstack/react-query";
import { RefreshCwIcon } from "lucide-react";
import { FC, useEffect, useRef, useState, useTransition } from "react";
import { useIntl } from "react-intl";
import {
  getGetPageTextContentQueryKey,
  useReprocessPageContent,
} from "~/api/documents/documents";
import type { ReportSectionMeta } from "~/api/model";
import { stripSentinelTags } from "~/constants";
import { useToast } from "~/hooks/use-toast";
import { trackEvent } from "~/utils/analytics";
import {
  BatesLink,
  SectionLink,
  useSourceMapDelegation,
} from "~/utils/markdownTerms";
import { ReportCopyExportActions } from "./ReportCopyExportActions";
import { UnifiedMarkdown } from "./UnifiedMarkdown";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { UserAvatar } from "./ui/user-avatar";

type MarkdownContentModalProps = {
  content: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  filename?: string | null;
  authorName?: string;
  uploadDate?: string;
  openEventName?: string;
  closeEventName?: string;
  copyEventName?: string;
  exportOpenEventName?: string;
  exportEventName?: string;
  terms: Array<{ term: string; definition: string }>;
  pageImageUrl?: string;
  pageRotation?: number;
  caseInfo?: {
    caseName: string;
    dateOfBirth: string;
    dateOfLoss: string;
    claimNumber: string;
  };
  organizationId?: string;
  projectId?: string;
  pageId?: string;
  onContentUpdate?: (newContent: string) => void;
  isViewOnly?: boolean;
  sectionLinks?: SectionLink[];
  onSectionClick?: (sectionId: string, firstPageId: string) => void;
  batesLinks?: BatesLink[];
  onBatesClick?: (pageId: string) => void;
  reportId?: string;
  reportSections?: ReportSectionMeta[];
};
export function decodeHtmlEntities(html: string): string {
  if (typeof document === "undefined") return html;
  const ta = document.createElement("textarea");
  ta.innerHTML = html;
  return ta.value;
}

function normalizeNewlines(v: string) {
  return v
    .replace(/\\r\\n/g, "\n")
    .replace(/\r\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\r/g, "\n");
}

const MULTIPLE_BLANK_LINES_RE = /\n{3,}/g;
const HTML_ENTITY_RE = /&(?:#\d+|[a-z]+);/i;

function decodeIfNeeded(value: string, shouldDecode: boolean) {
  if (!shouldDecode || !HTML_ENTITY_RE.test(value)) {
    return value;
  }
  return decodeHtmlEntities(value);
}

function sanitizeMarkdown(
  value: string,
  { decodeEntities = false }: { decodeEntities?: boolean } = {}
) {
  const decoded = decodeIfNeeded(value.trim(), decodeEntities);
  let out = normalizeNewlines(decoded);
  out = out.replace(MULTIPLE_BLANK_LINES_RE, "\n\n");
  return out.replace(/^\s*\n+/, "").replace(/\n+\s*$/, "");
}

function stripWrappingFenceMarkers(value: string) {
  if (!value.startsWith("```")) {
    return value;
  }

  const firstLineBreak = value.indexOf("\n");
  if (firstLineBreak === -1) {
    return "";
  }

  const withoutOpening = value.substring(firstLineBreak + 1);
  const lastFence = withoutOpening.lastIndexOf("```");

  if (lastFence === -1) {
    return withoutOpening;
  }

  return withoutOpening.substring(0, lastFence);
}

export function preprocessMarkdownContent(raw: string): string {
  let s = stripSentinelTags(raw ?? "");

  // Check if there's an opening fence
  if (s.includes("```")) {
    // Count the number of ``` occurrences
    const fenceCount = (s.match(/```/g) || []).length;
    // If odd number, we're missing a closing fence - add it
    if (fenceCount % 2 === 1) {
      s = s + "\n```";
    }
  }

  const MAX_SAFE_LENGTH = 100000;
  if (s.length > MAX_SAFE_LENGTH) {
    const stripped = stripWrappingFenceMarkers(s);
    return sanitizeMarkdown(stripped, { decodeEntities: true });
  }

  const hasPreTag = s.includes("<pre");
  const hasCodeTag = s.includes("<code");
  const hasFence = s.includes("```");

  // Check if entire content is wrapped in markdown fence/code blocks
  if (hasFence && s.startsWith("```")) {
    const match = s.match(/^```(?:markdown|md)?[ \t]*\n?([\s\S]*?)\n?```$/i);
    if (match) {
      return sanitizeMarkdown(match[1], { decodeEntities: true });
    }
  }

  if (hasPreTag && s.startsWith("<pre")) {
    const match = s.match(
      /^<pre[^>]*>\s*<code[^>]*class=["']?language-(?:markdown|md)["']?[^>]*>([\s\S]*?)<\/code>\s*<\/pre>$/i
    );
    if (match) {
      return sanitizeMarkdown(match[1], { decodeEntities: true });
    }
  }

  if (hasCodeTag && s.startsWith("<code")) {
    const match = s.match(
      /^<code[^>]*class=["']?language-(?:markdown|md)["']?[^>]*>([\s\S]*?)<\/code>$/i
    );
    if (match) {
      return sanitizeMarkdown(match[1], { decodeEntities: true });
    }
  }

  // If no wrapping pattern matched, pass content through as-is.
  // Internal code fences are valid markdown and should be rendered normally.
  return sanitizeMarkdown(s);
}

export const MarkdownContentModal: FC<MarkdownContentModalProps> = ({
  content,
  open,
  title,
  filename,
  authorName,
  uploadDate,
  onOpenChange,
  openEventName,
  closeEventName,
  copyEventName,
  exportOpenEventName,
  exportEventName,
  pageImageUrl,
  pageRotation = 0,
  caseInfo,
  organizationId,
  projectId,
  pageId,
  onContentUpdate,
  isViewOnly = false,
  onSectionClick,
  onBatesClick,
  reportId,
  reportSections,
}) => {
  const { formatMessage } = useIntl();
  const { toast } = useToast();

  const modalContentRef = useRef<HTMLDivElement>(null);
  useSourceMapDelegation(modalContentRef, onSectionClick, onBatesClick);
  const queryClient = useQueryClient();
  const [, startTransition] = useTransition();
  const [processedContent, setProcessedContent] = useState("");
  const reprocessMutation = useReprocessPageContent();

  const canReprocess = !!(organizationId && projectId && pageId && !isViewOnly);

  const handleReprocess = async () => {
    if (!canReprocess) return;

    trackEvent({
      eventName: "app/doc_viewer/recognize_modal/reprocess.button/click",
    });

    try {
      const result = await reprocessMutation.mutateAsync({
        organizationId,
        projectId,
        pageId,
      });
      if (result.content && onContentUpdate) {
        onContentUpdate(result.content);
      }
      queryClient.invalidateQueries({
        queryKey: getGetPageTextContentQueryKey(
          organizationId,
          projectId,
          pageId
        ),
      });
      toast({
        title: formatMessage({
          id: "docViewer.reprocess.success",
          defaultMessage: "Page reprocessed",
        }),
        description: formatMessage({
          id: "docViewer.reprocess.successDescription",
          defaultMessage:
            "The page content has been re-extracted using AI and updated.",
        }),
      });
    } catch {
      toast({
        variant: "destructive",
        title: formatMessage({
          id: "error",
          defaultMessage: "Error",
        }),
        description: formatMessage({
          id: "docViewer.reprocess.error",
          defaultMessage:
            "Failed to reprocess the page content. Please try again.",
        }),
      });
    }
  };

  useEffect(() => {
    if (content) {
      startTransition(() => {
        const processed = preprocessMarkdownContent(content);
        setProcessedContent(processed);
      });
    } else {
      setProcessedContent("");
    }
  }, [content]);

  useEffect(() => {
    if (closeEventName && openEventName) {
      trackEvent({ eventName: openEventName });
    } else if (closeEventName) {
      trackEvent({ eventName: closeEventName });
    }
  }, [closeEventName, openEventName, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`py-0 px-0 gap-0 flex h-[calc(100dvh-32px)] max-h-[calc(100dvh-32px)] ${pageImageUrl ? "w-[calc(100vw-32px)] max-w-none" : "w-full max-w-2xl"} flex-col`}
      >
        <div className="flex flex-1 overflow-hidden">
          <div
            className={`${pageImageUrl ? "border-r border-border" : ""} flex-1 overflow-y-auto overflow-x-hidden pt-6 px-6 rounded-md pb-2`}
          >
            <DialogHeader className="mb-2">
              <DialogTitle>{title}</DialogTitle>
              <DialogDescription className="sr-only">
                Maximized view of {title}
              </DialogDescription>
            </DialogHeader>
            {filename && (
              <div className="mb-4">
                <div className="text-xs text-muted-foreground mb-2">
                  <span className="font-medium">Source File:</span> {filename}
                </div>
                {(authorName || uploadDate) && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                    <span>Uploaded:</span>
                    {uploadDate && (
                      <span>{new Date(uploadDate).toLocaleDateString()}</span>
                    )}
                    {authorName && <UserAvatar name={authorName} size="sm" />}
                    {authorName && (
                      <span className="font-medium">{authorName}</span>
                    )}
                  </div>
                )}
                <hr className="border-border" />
              </div>
            )}
            <div ref={modalContentRef} style={{ textWrap: "wrap" }}>
              {!processedContent ? (
                <div className="flex items-center text-sm text-muted-foreground">
                  Loading content...
                </div>
              ) : (
                <UnifiedMarkdown
                  withHeadings
                  components={{
                    p: ({ node, ...props }) => <p {...props} />,
                  }}
                >
                  {processedContent}
                </UnifiedMarkdown>
              )}
            </div>
          </div>
          {pageImageUrl && (
            <div className="flex-1 overflow-y-auto bg-muted p-6 flex items-start justify-center">
              <img
                src={pageImageUrl}
                alt="Page scan"
                className="w-full h-auto object-contain"
                style={{
                  transform: `rotate(${pageRotation}deg)`,
                }}
              />
            </div>
          )}
        </div>
        <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-2 px-4 sm:px-6 py-3 border-t border-border flex-shrink-0">
          <div className="flex flex-wrap gap-2">
            <ReportCopyExportActions
              content={content}
              reportId={reportId}
              reportSections={reportSections}
              fileName={filename || "Markdown Content"}
              title={title}
              copyEventName={copyEventName}
              exportOpenEventName={exportOpenEventName}
              exportEventName={exportEventName}
              caseInfo={caseInfo}
            />
            {canReprocess && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReprocess}
                disabled={reprocessMutation.isPending}
              >
                <RefreshCwIcon
                  className={`w-4 h-4 mr-2 ${reprocessMutation.isPending ? "animate-spin" : ""}`}
                />
                {reprocessMutation.isPending
                  ? formatMessage({
                      id: "docViewer.reprocess.loading",
                      defaultMessage: "Reprocessing...",
                    })
                  : formatMessage({
                      id: "docViewer.reprocess.button",
                      defaultMessage: "Reprocess",
                    })}
              </Button>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            {formatMessage({
              id: "close",
              defaultMessage: "Close",
            })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
